// S-B3/S-B4/S-B6/S-B7: Company Bank Accounts Section
// Admin-facing section for managing company bank account connections

import React, { useState, useEffect } from 'react';
import { Card, Button, Empty, Space, Alert, List, message, Spin, Tag, Modal, Select, Input } from 'antd';
import { BankOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, ReloadOutlined, StarOutlined, StarFilled, DisconnectOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePlaidLink } from 'react-plaid-link';
import { useFeature } from '../contexts/FeatureFlagsContext';
import CompanyBankAccountConnect from '../components/CompanyBankAccountConnect';
import { companyPaymentsEndpoints, type BankAccount } from '../api/companyPaymentsEndpoints';

interface CompanyBankAccountsSectionProps {
  hasLinkedAccount?: boolean;
  onAccountLinked?: () => void;
}

const CompanyBankAccountsSection: React.FC<CompanyBankAccountsSectionProps> = ({
  hasLinkedAccount = false,
  onAccountLinked
}) => {
  const { t } = useTranslation('payments');
  const isPlaidEnabled = useFeature('plaid');

  // Don't render if Plaid is disabled
  if (!isPlaidEnabled) {
    return null;
  }
  const [showConnectFlow, setShowConnectFlow] = useState(false);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [settingDefault, setSettingDefault] = useState<string | null>(null);
  const [showReconnectModal, setShowReconnectModal] = useState(false);
  const [reconnectLinkToken, setReconnectLinkToken] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  
  // S-B7: Disable bank account
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [accountToDisable, setAccountToDisable] = useState<BankAccount | null>(null);
  const [disableReason, setDisableReason] = useState<string>('');
  const [disableReasonText, setDisableReasonText] = useState<string>('');
  const [replacementDefaultId, setReplacementDefaultId] = useState<string | undefined>(undefined);
  const [disabling, setDisabling] = useState(false);

  // S-B6: Plaid Link for reconnection
  const { open: openReconnect, ready: reconnectReady } = usePlaidLink({
    token: reconnectLinkToken,
    onSuccess: async (publicToken, metadata) => {
      try {
        setReconnecting(true);
        await companyPaymentsEndpoints.exchangeCompanyPublicToken(publicToken);
        // Sync accounts after reconnection
        const response = await companyPaymentsEndpoints.syncCompanyBankAccounts();
        setAccounts(response.accounts);
        message.success(t('companyBankAccounts.reconnect.success'));
        setShowReconnectModal(false);
      } catch (err: any) {
        console.error('Failed to reconnect company bank:', err);
        message.error(t('companyBankAccounts.reconnect.error'));
      } finally {
        setReconnecting(false);
      }
    },
    onExit: (err, metadata) => {
      setReconnecting(false);
      if (err != null) {
        console.error('Company reconnect error:', err);
      }
    },
  });

  useEffect(() => {
    if (hasLinkedAccount) {
      fetchAccounts();
    }
  }, [hasLinkedAccount]);

  // S-B6: Auto-launch Plaid Link when reconnect token is ready
  useEffect(() => {
    if (reconnectLinkToken && reconnectReady && !reconnecting) {
      openReconnect();
    }
  }, [reconnectLinkToken, reconnectReady]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await companyPaymentsEndpoints.listCompanyBankAccounts();
      setAccounts(response.accounts);
    } catch (err: any) {
      console.error('Failed to fetch company bank accounts:', err);
      message.error(t('companyBankAccounts.list.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await companyPaymentsEndpoints.syncCompanyBankAccounts();
      setAccounts(response.accounts);
      message.success(t('companyBankAccounts.list.syncSuccess'));
    } catch (err: any) {
      console.error('Failed to sync company bank accounts:', err);
      message.error(t('companyBankAccounts.list.syncError'));
    } finally {
      setSyncing(false);
    }
  };

  const handleSuccess = async () => {
    setShowConnectFlow(false);
    onAccountLinked?.();
    // Auto-sync after connection
    try {
      const response = await companyPaymentsEndpoints.syncCompanyBankAccounts();
      setAccounts(response.accounts);
    } catch (err: any) {
      console.error('Failed to auto-sync:', err);
    }
  };

  const handleCancel = () => {
    setShowConnectFlow(false);
  };

  const handleSetDefault = async (accountId: string) => {
    try {
      setSettingDefault(accountId);
      await companyPaymentsEndpoints.setDefaultCompanyBankAccount(accountId);
      message.success(t('companyBankAccounts.default.setSuccess'));
      // Refresh accounts to update default flag
      await fetchAccounts();
    } catch (err: any) {
      console.error('Failed to set default company bank account:', err);
      message.error(t('companyBankAccounts.default.setError'));
    } finally {
      setSettingDefault(null);
    }
  };

  // S-B6: Handle reconnect flow
  const handleReconnect = async () => {
    if (reconnecting) return; // Prevent multiple clicks
    
    try {
      const response = await companyPaymentsEndpoints.createCompanyUpdateLinkToken();
      setReconnectLinkToken(response.link_token);
      setShowReconnectModal(true);
    } catch (err: any) {
      console.error('Failed to create company update link token:', err);
      message.error(t('companyBankAccounts.reconnect.error'));
    }
  };

  const handleReconnectConfirm = () => {
    // Plaid will auto-launch via useEffect when token is ready
    setReconnecting(true);
  };

  const handleReconnectCancel = () => {
    setShowReconnectModal(false);
    setReconnectLinkToken(null);
    setReconnecting(false);
  };

  // S-B7: Disable bank account handlers
  const handleDisableClick = (account: BankAccount) => {
    setAccountToDisable(account);
    setDisableReason('');
    setDisableReasonText('');
    setReplacementDefaultId(undefined);
    setShowDisableModal(true);
  };

  const handleDisableConfirm = async () => {
    if (!accountToDisable || !disableReason) {
      message.error(t('companyBankAccounts.disable.error'));
      return;
    }

    try {
      setDisabling(true);
      await companyPaymentsEndpoints.disableCompanyBankAccount(accountToDisable.id, {
        reason_code: disableReason,
        reason_text: disableReasonText || undefined,
        replacement_default_bank_account_id: replacementDefaultId
      });
      
      message.success(t('companyBankAccounts.disable.success'));
      setShowDisableModal(false);
      setAccountToDisable(null);
      setDisableReason('');
      setDisableReasonText('');
      setReplacementDefaultId(undefined);
      
      // Refresh accounts list
      await fetchAccounts();
    } catch (err: any) {
      console.error('Failed to disable company bank account:', err);
      const errorMessage = err.response?.data?.error?.includes('active or pending transfers')
        ? t('companyBankAccounts.disable.errorActiveTransfers')
        : t('companyBankAccounts.disable.error');
      message.error(errorMessage);
    } finally {
      setDisabling(false);
    }
  };

  const handleDisableCancel = () => {
    setShowDisableModal(false);
    setAccountToDisable(null);
    setDisableReason('');
    setDisableReasonText('');
    setReplacementDefaultId(undefined);
  };

  // Check if any account is disconnected
  const hasDisconnectedAccounts = accounts.some(acc => acc.status === 'disconnected');
  
  // S-E1: Check if default bank account is set
  const hasDefaultBankAccount = accounts.some(acc => acc.is_default);
  const hasActiveAccounts = accounts.some(acc => acc.status === 'active');

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Tag color="success" icon={<CheckCircleOutlined />}>{t('bankAccounts.status.active')}</Tag>;
      case 'disconnected':
        return <Tag color="error" icon={<CloseCircleOutlined />}>{t('bankAccounts.status.disconnected')}</Tag>;
      case 'disabled':
        return <Tag color="default">{t('bankAccounts.status.disabled')}</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getAccountTypeLabel = (type: string) => {
    const key = `accountTypes.${type}`;
    return t(key, type);
  };

  if (showConnectFlow) {
    return (
      <Card 
        title={
          <Space>
            <BankOutlined />
            {t('companyBankAccounts.connectTitle')}
          </Space>
        }
        variant="borderless"
        style={{ marginBottom: 24 }}
      >
        <Alert
          title={t('companyBankAccounts.warning.title')}
          description={t('companyBankAccounts.warning.description')}
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
        />
        <CompanyBankAccountConnect
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </Card>
    );
  }

  if (hasLinkedAccount) {
    return (
      <Card
        title={
          <Space>
            <BankOutlined />
            {t('companyBankAccounts.title')}
          </Space>
        }
        variant="borderless"
        style={{ marginBottom: 24 }}
        extra={
          <Space>
            <Button
              icon={<BankOutlined />}
              onClick={() => setShowConnectFlow(true)}
            >
              {t('companyBankAccounts.list.addAccountButton')}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleSync}
              loading={syncing}
            >
              {t('companyBankAccounts.list.syncButton')}
            </Button>
          </Space>
        }
      >
        <Spin spinning={loading}>
          {/* S-E1: Warning banner if no default bank account is set */}
          {hasActiveAccounts && !hasDefaultBankAccount && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={t('companyBankAccounts.default.missing.title')}
              description={t('companyBankAccounts.default.missing.description')}
              style={{ marginBottom: 16 }}
            />
          )}
          
          {accounts.length > 0 ? (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <List
                dataSource={accounts}
                renderItem={(account, index) => {
                  // Build actions array based on status
                  const actions = [];
                  
                  // S-B6: Show reconnect button only on first disconnected account (all share same Plaid item)
                  const isFirstDisconnected = account.status === 'disconnected' && 
                    accounts.findIndex(acc => acc.status === 'disconnected') === index;
                  
                  if (isFirstDisconnected) {
                    actions.push(
                      <Button
                        type="primary"
                        danger
                        icon={<DisconnectOutlined />}
                        onClick={handleReconnect}
                        disabled={reconnecting}
                      >
                        {t('companyBankAccounts.reconnect.button')}
                      </Button>
                    );
                  } else if (account.is_default) {
                    actions.push(
                      <Tag color="gold" icon={<StarFilled />}>
                        {t('companyBankAccounts.default.badge')}
                      </Tag>
                    );
                  } else if (account.status === 'active' || account.status === 'disconnected') {
                    actions.push(
                      <Button
                        type="link"
                        icon={<StarOutlined />}
                        onClick={() => handleSetDefault(account.id)}
                        loading={settingDefault === account.id}
                        disabled={account.status !== 'active'}
                      >
                        {t('companyBankAccounts.default.setButton')}
                      </Button>
                    );
                  }
                  
                  // S-B7: Add disable button (not for already disabled accounts)
                  if (account.status !== 'disabled') {
                    actions.push(
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDisableClick(account)}
                      >
                        {t('companyBankAccounts.disable.button')}
                      </Button>
                    );
                  }

                  return (
                <List.Item actions={actions}>
                  <List.Item.Meta
                    avatar={<BankOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                    title={
                      <Space>
                        <span>{account.institution_name}</span>
                        {getStatusBadge(account.status)}
                      </Space>
                    }
                    description={
                      <Space orientation="vertical" size={4}>
                        <div>
                          <strong>{account.account_name}</strong>
                          {account.mask && <span style={{ marginLeft: 8, color: '#999' }}>••••{account.mask}</span>}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                          {getAccountTypeLabel(account.account_type)}
                        </div>
                      </Space>
                    }
                  />
                </List.Item>
                  );
                }}
            />
            </div>
          ) : (
            <Empty
              description={t('companyBankAccounts.list.noAccounts')}
              style={{ padding: '2rem 0' }}
            >
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleSync}
                loading={syncing}
              >
                {t('companyBankAccounts.list.syncButton')}
              </Button>
            </Empty>
          )}
        </Spin>

        {/* S-B6: Reconnect Modal */}
        <Modal
          title={t('companyBankAccounts.reconnect.modal.title')}
          open={showReconnectModal}
          onOk={handleReconnectConfirm}
          onCancel={handleReconnectCancel}
          okText={t('companyBankAccounts.reconnect.modal.confirm')}
          cancelText={t('companyBankAccounts.reconnect.modal.cancel')}
          confirmLoading={reconnecting}
        >
          <p>{t('companyBankAccounts.reconnect.modal.description')}</p>
        </Modal>

        {/* S-B7: Disable Modal */}
        <Modal
          title={t('companyBankAccounts.disable.modal.title')}
          open={showDisableModal}
          onOk={handleDisableConfirm}
          onCancel={handleDisableCancel}
          okText={t('companyBankAccounts.disable.modal.confirm')}
          cancelText={t('companyBankAccounts.disable.modal.cancel')}
          confirmLoading={disabling}
          okButtonProps={{ disabled: !disableReason }}
        >
          <Space orientation="vertical" style={{ width: '100%' }} size="large">
            <div>
              <p>{t('companyBankAccounts.disable.modal.description')}</p>
            </div>

            {accountToDisable?.is_default && (
              <Alert
                type="warning"
                message={t('companyBankAccounts.disable.modal.note')}
                showIcon
              />
            )}

            <div>
              <div style={{ marginBottom: 8 }}>
                <label>{t('companyBankAccounts.disable.modal.reasonLabel')}</label>
              </div>
              <Select
                style={{ width: '100%' }}
                placeholder={t('companyBankAccounts.disable.modal.reasonPlaceholder')}
                value={disableReason}
                onChange={(value) => setDisableReason(value)}
              >
                <Select.Option value="no_longer_used">
                  {t('companyBankAccounts.disable.reasons.no_longer_used')}
                </Select.Option>
                <Select.Option value="wrong_account">
                  {t('companyBankAccounts.disable.reasons.wrong_account')}
                </Select.Option>
                <Select.Option value="security_concern">
                  {t('companyBankAccounts.disable.reasons.security_concern')}
                </Select.Option>
                <Select.Option value="duplicate">
                  {t('companyBankAccounts.disable.reasons.duplicate')}
                </Select.Option>
                <Select.Option value="other">
                  {t('companyBankAccounts.disable.reasons.other')}
                </Select.Option>
              </Select>
            </div>

            <div>
              <div style={{ marginBottom: 8 }}>
                <label>{t('companyBankAccounts.disable.modal.reasonTextLabel')}</label>
              </div>
              <Input.TextArea
                rows={3}
                placeholder={t('companyBankAccounts.disable.modal.reasonTextPlaceholder')}
                value={disableReasonText}
                onChange={(e) => setDisableReasonText(e.target.value)}
              />
            </div>

            {accountToDisable?.is_default && (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <label>{t('companyBankAccounts.disable.modal.replacementLabel')}</label>
                </div>
                <Select
                  style={{ width: '100%' }}
                  placeholder={t('companyBankAccounts.disable.modal.replacementPlaceholder')}
                  value={replacementDefaultId}
                  onChange={(value) => setReplacementDefaultId(value)}
                  allowClear
                >
                  {accounts
                    .filter(acc => 
                      acc.id !== accountToDisable.id && 
                      acc.status === 'active'
                    )
                    .map(acc => (
                      <Select.Option key={acc.id} value={acc.id}>
                        {acc.institution_name} - {acc.account_name} ({acc.mask})
                      </Select.Option>
                    ))
                  }
                </Select>
              </div>
            )}
          </Space>
        </Modal>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <BankOutlined />
          {t('companyBankAccounts.title')}
        </Space>
      }
      variant="borderless"
      style={{ marginBottom: 24 }}
    >
      <Alert
        message={t('companyBankAccounts.warning.title')}
        description={t('companyBankAccounts.warning.description')}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Empty
        image={<BankOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
        description={
          <Space orientation="vertical">
            <span style={{ fontWeight: 500 }}>{t('companyBankAccounts.emptyState.title')}</span>
            <span style={{ fontSize: '0.9em', color: '#999' }}>
              {t('companyBankAccounts.emptyState.description')}
            </span>
          </Space>
        }
      >
        <Button
          type="primary"
          icon={<BankOutlined />}
          onClick={() => setShowConnectFlow(true)}
        >
          {t('companyBankAccounts.emptyState.connectButton')}
        </Button>
      </Empty>
    </Card>
  );
};

export default CompanyBankAccountsSection;
